// Invoices: the tiles, the list and Send many.
import { type InvoiceFilter, invoiceRank, type InvoiceRow, type SendPreview } from "@steward/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Fragment, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useInvoices, useMergeInvoices, useSendInvoice, type Via } from "../../api/money";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { Choice } from "../../components/fields";
import { money, moneyExact, plural, shortDate, todayIn, trend } from "../../components/format";
import { Icon } from "../../components/Icon";
import { FilterPick, Modal, useToast } from "../../components/overlays";
import { Button, EmptyState, LoadError, Page, Skeleton, type Status } from "../../components/parts";
import { errText } from "../../components/work";
import { SettingsButton } from "../settings/Feature";
import { dayIn, dueOf, InvoiceStatus, Pick, Range, rangeDays, RANGES } from "./common";
import { NewInvoice } from "./New";
import { mergeHint } from "./Page";

/**
 * The top cards. Overview is Jobber's (a dot, the count and the money for each status) with the old Steward's lines (Bills
 * at month end, To collect, Payment on the way), each one filtering the list, and what is waiting on payment under it.
 * Issued, Average invoice and Payment time are Jobber's, each against the 30 days before so a slow month shows.
 */
function InvoiceTiles({ companyId, tab, setTab }: { companyId: string; tab: Pick; setTab: (t: Pick) => void }) {
  const sum = useInvoices(companyId, { state: "all" }).data?.summary;
  const wait = <span className="skeleton inline" />;
  const lines: { key: InvoiceFilter | null; words: string; n: number; cents: number | null; look: Status }[] = sum
    ? [
        { key: "to_check", words: "To check", n: sum.toCheck, cents: sum.toCheckCents, look: "informative" },
        ...(sum.monthEnd
          ? [{ key: null, words: "Bills at month end", n: sum.monthEnd, cents: null, look: "inactive" as Status }]
          : []),
        { key: "to_collect", words: "To collect", n: sum.toCollect, cents: sum.toCollectCents, look: "warning" },
        { key: "awaiting", words: "Awaiting payment", n: sum.awaiting, cents: sum.awaitingCents, look: "warning" },
        ...(sum.onTheWay
          ? [
              {
                key: "on_the_way" as InvoiceFilter,
                words: "Payment on the way",
                n: sum.onTheWay,
                cents: sum.onTheWayCents,
                look: "informative" as Status,
              },
            ]
          : []),
        { key: "past_due", words: "Past due", n: sum.pastDue, cents: sum.pastDueCents, look: "critical" },
      ]
    : [];
  const avg = sum?.issuedCount ? Math.round(sum.issuedCents / sum.issuedCount) : null;
  const avgBefore = sum?.issuedBeforeCount ? Math.round(sum.issuedBeforeCents / sum.issuedBeforeCount) : null;
  const issued = sum ? trend(sum.issuedCount, sum.issuedBeforeCount) : null;
  const average = avg !== null && avgBefore !== null ? trend(avg, avgBefore) : null;
  const payTime =
    typeof sum?.payDays === "number" && typeof sum.payDaysBefore === "number"
      ? trend(sum.payDays, sum.payDaysBefore, { lowerIsBetter: true })
      : null;
  return (
    <div className="tiles inv-tiles">
      <div className="tile">
        <span className="label">Overview</span>
        {sum ? (
          <div className="ov-rows">
            {lines.map((o) => {
              const k = o.key;
              const words = (
                <>
                  <i className={`ov-dot ${o.look}`} aria-hidden="true" />
                  {o.words} ({o.n})
                </>
              );
              return (
                <Fragment key={o.words}>
                  {k ? (
                    <button type="button" aria-pressed={tab === k} onClick={() => setTab(tab === k ? "all" : k)}>
                      {words}
                    </button>
                  ) : (
                    <span>{words}</span>
                  )}
                  <span className="num muted">{o.cents !== null && o.n ? money(o.cents) : ""}</span>
                </Fragment>
              );
            })}
          </div>
        ) : (
          <Skeleton rows={4} />
        )}
        {sum ? (
          <span className="hint">
            {money(sum.owedCents)} waiting on payment, {plural(sum.owedCount, "invoice")} sent
          </span>
        ) : null}
      </div>
      <div className="tile">
        <span className="label">Issued</span>
        <span className="value">{sum ? sum.issuedCount : wait}</span>
        <span className="hint">{sum ? `Past 30 days, ${moneyExact(sum.issuedCents)}` : ""}</span>
        {issued ? <span className={`hint ${issued.cls}`}>{issued.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Average invoice</span>
        <span className="value">{sum ? (avg === null ? "None yet" : moneyExact(avg)) : wait}</span>
        <span className="hint">Past 30 days</span>
        {average ? <span className={`hint ${average.cls}`}>{average.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Payment time</span>
        <span className="value">
          {sum ? (sum.payDays === null ? "None yet" : `${String(sum.payDays)} ${sum.payDays === 1 ? "day" : "days"}`) : wait}
        </span>
        <span className="hint">From sent to paid, past 30 days</span>
        {payTime ? <span className={`hint ${payTime.cls}`}>{payTime.text}</span> : null}
      </div>
    </div>
  );
}

/**
 * Send many To check invoices in one go (owner, 2026-10-31: "I can't just go through a bunch and send"). Each goes to the
 * client's own number or email, the one the Send box picks first; one with no way to reach them, or a warning like a
 * number another client shares, stays in To check to open and send by hand.
 */
function SendMany({ ids, onClose }: { ids: string[] | "all"; onClose: (sent: boolean) => void }) {
  const { company } = useSession();
  const list = useInvoices(company.id, { state: "to_check" });
  const send = useSendInvoice();
  const all = (list.data?.invoices ?? []).filter((r) => r.status === "to_check");
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const on = picked ?? new Set(ids === "all" ? all.map((r) => r.id) : ids);
  const chosen = all.filter((r) => on.has(r.id));
  const [via, setVia] = useState<Via>("text");
  const [run, setRun] = useState<{ done: number; sent: number; total: number; left: { name: string; why: string }[] } | null>(
    null,
  );
  const busy = run !== null && run.done < run.total;
  const toggle = (id: string) => {
    const next = new Set(on);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const go = async () => {
    const left: { name: string; why: string }[] = [];
    const list = [...chosen];
    let sent = 0;
    setRun({ done: 0, sent, total: list.length, left });
    for (const [n, r] of list.entries()) {
      try {
        const p = await api<SendPreview>("GET", `invoices/${r.id}/send?via=${via}`);
        const to = p.to ?? p.choices[0];
        if (!to) left.push({ name: r.clientName, why: via === "text" ? "No phone number" : "No email" });
        else if (p.warnings.length) left.push({ name: r.clientName, why: p.warnings[0] ?? "Needs a look" });
        else {
          await send.mutateAsync({ id: r.id, via, to });
          sent += 1;
        }
      } catch (e) {
        left.push({ name: r.clientName, why: errText(e, "That one didn't send.") });
      }
      setRun({ done: n + 1, sent, total: list.length, left: [...left] });
    }
  };
  const total = chosen.reduce((t, r) => t + r.totalCents - r.tipCents, 0);
  return (
    <Modal
      title="Check and send"
      open
      size="full"
      onClose={() => (busy ? undefined : onClose(run !== null))}
      footer={
        run ? (
          <Button disabled={busy} onClick={() => onClose(true)}>
            Done
          </Button>
        ) : (
          <>
            <Button type="tertiary" onClick={() => onClose(false)}>
              Cancel
            </Button>
            <Button icon="mail" disabled={!chosen.length} onClick={() => void go()}>
              {chosen.length ? `Send ${plural(chosen.length, "invoice")}` : "Pick invoices to send"}
            </Button>
          </>
        )
      }
    >
      {run ? (
        <div className="send-many-run" aria-live="polite">
          <b>{busy ? `Sending ${String(run.done + 1)} of ${String(run.total)}` : `${plural(run.sent, "invoice")} sent`}</b>
          <div className="send-many-bar">
            <span style={{ width: `${String(Math.round((run.done / Math.max(1, run.total)) * 100))}%` }} />
          </div>
          {run.left.length ? (
            <>
              <p className="muted small">These stay in To check. Open each one to send it.</p>
              <div className="todo">
                {run.left.map((l, k) => (
                  <div className="item" key={`${l.name}-${String(k)}`}>
                    <div className="what">
                      <b>{l.name}</b>
                      <span>{l.why}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="stack">
          <Choice<Via>
            label="Send by"
            value={via}
            onChange={setVia}
            options={[
              { value: "text", label: "Text" },
              { value: "email", label: "Email, with the PDF" },
            ]}
          />
          {list.isLoading ? (
            <Skeleton rows={4} />
          ) : all.length ? (
            <>
              <div className="row between">
                <span className="muted small">
                  {plural(chosen.length, "invoice")} · {moneyExact(total)}
                </span>
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => setPicked(chosen.length === all.length ? new Set() : new Set(all.map((r) => r.id)))}
                >
                  {chosen.length === all.length ? "Untick all" : "Tick all"}
                </Button>
              </div>
              <div className="todo send-many-list">
                {all.map((r) => (
                  <label className="item" key={r.id}>
                    <input type="checkbox" checked={on.has(r.id)} onChange={() => toggle(r.id)} />
                    <div className="what">
                      <b>{r.clientName}</b>
                      <span>
                        {[r.subject, r.firstServiceDate ? shortDate(r.firstServiceDate) : null].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    <span className="num">{moneyExact(r.totalCents - r.tipCents)}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <p className="muted">Nothing is waiting in To check.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** The list, filling the screen like the old Steward's (owner, 2026-10-29), with Jobber's top cards and filter pills. */
export function InvoicesList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const today = todayIn(company.timezone);
  const [tab, setTab] = useState<Pick>("all");
  const [range, setRange] = useState<Range>("all");
  const [typed, setTyped] = useState("");
  const list = useInvoices(company.id, {
    state: tab,
    ...rangeDays(range, today),
    ...(typed.trim() ? { q: typed.trim() } : {}),
  });
  const counts = list.data?.counts;
  const sum = list.data?.summary;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const merge = useMergeInvoices();
  const toast = useToast();
  const pickedRows = (list.data?.invoices ?? []).filter((r) => picked.has(r.id));
  const mergeWhy = mergeHint(pickedRows, can("invoice.edit_paid"));
  const [making, setMaking] = useState<false | { clientId: string }>(false);
  const [sendMany, setSendMany] = useState<string[] | "all" | null>(null);
  const toSend = pickedRows.filter((r) => r.status === "to_check");
  const toCheckN = counts?.to_check ?? 0;
  // Create, Invoice in the side menu lands here with ?new=true (and a client page's + with its clientId): open the box,
  // then drop it from the address.
  const search = useSearch({ strict: false });
  useEffect(() => {
    if (!search.new) return;
    setMaking({ clientId: search.clientId ?? "" });
    void navigate({ to: "/invoices", search: {}, replace: true });
  }, [search.new, search.clientId, navigate]);
  const picks: { value: Pick; label: string; count?: number }[] = [
    { value: "all", label: "All" },
    ...(
      [
        ["to_check", "To check", counts?.to_check],
        ["to_collect", "To collect", sum?.toCollect],
        ["awaiting", "Awaiting payment", counts?.awaiting],
        ...(sum?.onTheWay ? [["on_the_way", "Payment on the way", sum.onTheWay] as const] : []),
        ["past_due", "Past due", counts?.past_due],
        ["debt", "Debt", counts?.debt],
        ["paid", "Paid", counts?.paid],
        ["closed", "Archived", counts?.closed],
      ] as const
    ).map(([value, label, n]) => ({ value, label, ...(n !== undefined ? { count: n } : {}) })),
  ];
  const filtered = tab !== "all" || range !== "all" || typed.trim() !== "";
  return (
    <Page
      title="Invoices"
      secondaryAction={<SettingsButton to="/invoices/settings">Invoice settings</SettingsButton>}
      primaryAction={
        can("invoice.update") ? (
          <Button icon="plus" onClick={() => setMaking({ clientId: "" })}>
            New invoice
          </Button>
        ) : null
      }
    >
      {making ? (
        <NewInvoice
          clientId={making.clientId}
          onClose={() => setMaking(false)}
          onMade={(id, send) =>
            void navigate({ to: "/invoices/$id", params: { id }, search: send ? { then: "send" as const } : {} })
          }
        />
      ) : null}
      {sendMany ? (
        <SendMany
          ids={sendMany}
          onClose={(sent) => {
            setSendMany(null);
            if (sent) setPicked(new Set());
          }}
        />
      ) : null}
      <InvoiceTiles companyId={company.id} tab={tab} setTab={setTab} />
      <DataTable<InvoiceRow>
        title="All invoices"
        rows={list.data?.invoices ?? []}
        rowKey={(i) => i.id}
        loading={list.isFetching}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Search invoices" }}
        onRowClick={(i) => void navigate({ to: "/invoices/$id", params: { id: i.id } })}
        {...(can("invoice.update") ? { selected: picked, onSelect: setPicked } : {})}
        bulk={
          <>
            {can("invoice.send") && toSend.length ? (
              <Button size="small" icon="mail" onClick={() => setSendMany(toSend.map((r) => r.id))}>
                {`Send ${String(toSend.length)}`}
              </Button>
            ) : null}
            {mergeWhy ? <span className="muted small">{mergeWhy}</span> : null}
            <Button
              size="small"
              icon="merge"
              disabled={mergeWhy !== null}
              loading={merge.isPending}
              onClick={() =>
                merge.mutate([...picked], {
                  onSuccess: (d) => {
                    setPicked(new Set());
                    toast(`Put on one invoice${d.number ? ` #${String(d.number)}` : ""}`);
                    void navigate({ to: "/invoices/$id", params: { id: d.id } });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                })
              }
            >
              Merge
            </Button>
          </>
        }
        tools={
          <div className="chips">
            {can("invoice.send") && toCheckN ? (
              <Button size="small" icon="mail" onClick={() => setSendMany("all")}>
                {`Check and send ${String(toCheckN)}`}
              </Button>
            ) : null}
            <FilterPick<Pick> label="Status" value={tab} onChange={setTab} options={picks} />
            <FilterPick<Range>
              label="Date"
              value={range}
              onChange={setRange}
              options={RANGES.map(([value, label]) => ({ value, label }))}
            />
          </div>
        }
        empty={
          <EmptyState icon="invoices" title={filtered ? "No invoices match" : "No invoices yet"}>
            {filtered
              ? "Try another status, time or search."
              : "When a visit is marked done it lands here in To check. Check it, then send it."}
          </EmptyState>
        }
        phoneCard={(i) => {
          // Jobber's phone list (owner shot, 2026-10-31): name and status, then "Oct 21 | $300 | Cleaning Services".
          const issued = i.sentAt ? dayIn(company.timezone, i.sentAt) : i.firstServiceDate;
          return (
            <div className="row-card with-icon">
              <Icon name="invoices" />
              <div>
                <div className="top">
                  <b>{i.clientName}</b>
                  <InvoiceStatus i={i} />
                </div>
                <span className="sub">
                  {[issued ? shortDate(issued) : null, money(i.totalCents - i.tipCents), i.subject || null]
                    .filter(Boolean)
                    .join(" | ")}
                </span>
              </div>
            </div>
          );
        }}
        columns={[
          {
            key: "client",
            header: "Client",
            sort: (i) => i.clientName.toLowerCase(),
            render: (i) => <b>{i.clientName}</b>,
          },
          {
            key: "number",
            header: "Invoice number",
            sort: (i) => i.number ?? 0,
            render: (i) => <span className="num">{i.number ? `#${String(i.number)}` : "-"}</span>,
          },
          {
            key: "date",
            header: "Issued",
            sort: (i) => i.sentAt ?? i.firstServiceDate ?? "",
            render: (i) => {
              const d = i.sentAt ? dayIn(company.timezone, i.sentAt) : i.firstServiceDate;
              return d ? shortDate(d) : "-";
            },
          },
          {
            key: "due",
            header: "Due",
            sort: (i) => i.dueAt ?? "",
            render: (i) => {
              if (i.state === "to_check") return <span className="muted">-</span>;
              const d = dueOf(i, company.timezone, today);
              return <span className={d.late ? "late-words" : i.balanceCents > 0 ? undefined : "muted"}>{d.text}</span>;
            },
          },
          {
            key: "subject",
            header: "Subject",
            sort: (i) => i.subject.toLowerCase(),
            render: (i) => (
              <span className="muted">
                {i.subject || "-"}
                {i.visitCount > 1 ? ` · ${plural(i.visitCount, "visit")}` : ""}
              </span>
            ),
          },
          { key: "state", header: "Status", sort: invoiceRank, render: (i) => <InvoiceStatus i={i} /> },
          {
            key: "total",
            header: "Total",
            align: "right",
            sort: (i) => i.totalCents - i.tipCents,
            render: (i) => (
              <span className="num money-cell">
                <b>{moneyExact(i.totalCents - i.tipCents)}</b>
                {i.tipCents > 0 ? <span className="tip-note">Tip {moneyExact(i.tipCents)}</span> : null}
              </span>
            ),
          },
          {
            key: "balance",
            header: "Balance",
            align: "right",
            sort: (i) => i.balanceCents,
            render: (i) => <span className="num">{moneyExact(i.balanceCents)}</span>,
          },
        ]}
      />
    </Page>
  );
}

// One invoice --------------------------------------------------------------------------------------------------------
